import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Plus, Trash2, Edit3, Shield, X, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import SignatureBuilderDialog, { madeInBuilder } from '../../components/signatures/SignatureBuilderDialog';
import { VisibilityCards } from '../../components/sharing/SharingPicker';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SignatureTemplatesTab() {
    const { getAuthHeader } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [form, setForm] = useState({ name: '', content: '' });
    const [signatureData, setSignatureData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activeOverrideId, setActiveOverrideId] = useState(null);
    const [editorKey, setEditorKey] = useState(0);
    const [htmlOnly, setHtmlOnly] = useState(false);

    useEffect(() => { loadTemplates(); }, []);

    // The admin list is a plain list; the active override comes from GET /api/signatures,
    // the same value every composer seeds from (#989). A failed override read must not
    // hide the templates, so it falls back to "none active".
    const loadTemplates = async () => {
        try {
            const [listRes, sigRes] = await Promise.all([
                axios.get(`${API}/admin/signatures`, { headers: getAuthHeader() }),
                axios.get(`${API}/signatures`, { headers: getAuthHeader() }).catch(() => null),
            ]);
            setTemplates(Array.isArray(listRes.data) ? listRes.data : []);
            setActiveOverrideId(sigRes?.data?.active_override_id ?? null);
        } catch { toast.error('Failed to load signature templates'); }
        finally { setLoading(false); }
    };

    const handleSetOverride = async (signatureId) => {
        try {
            await axios.post(`${API}/admin/signatures/${signatureId}/set-override`, {}, { headers: getAuthHeader() });
            toast.success('Override set — new emails now start with this signature for everyone. People can still pick another or None.');
            loadTemplates();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to set override');
        }
    };

    const handleClearOverride = async () => {
        try {
            await axios.delete(`${API}/admin/signatures/override`, { headers: getAuthHeader() });
            toast.success('Override removed — people\'s own default signatures apply again.');
            loadTemplates();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to clear override');
        }
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            return toast.error('Please provide a template name');
        }
        let content = form.content;
        if (!content?.trim() && signatureData) {
            try {
                const res = await axios.post(`${API}/admin/signatures/render-html`, signatureData, { headers: getAuthHeader() });
                content = res.data?.html || '';
            } catch {
                const f = signatureData.fields || {};
                const parts = [f.name, f.title, f.company, f.email].filter(Boolean);
                if (parts.length) {
                    content = `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,sans-serif"><tr><td><strong>${f.name || ''}</strong></td></tr>${f.title || f.company ? `<tr><td>${[f.title, f.company].filter(Boolean).join(' · ')}</td></tr>` : ''}${f.email ? `<tr><td><a href="mailto:${f.email}">${f.email}</a></td></tr>` : ''}</table>`;
                }
            }
        }
        if (!content?.trim()) {
            return toast.error('Signature content is empty — fill in at least a name or title');
        }
        setSaving(true);
        try {
            const payload = { name: form.name, content };
            if (signatureData) payload.signature_data = signatureData;
            if (editingTemplate) {
                await axios.put(
                    `${API}/admin/signatures/${editingTemplate.signature_id}`,
                    payload,
                    { headers: getAuthHeader() }
                );
                toast.success('Template updated');
            } else {
                await axios.post(`${API}/admin/signatures`, payload, { headers: getAuthHeader() });
                toast.success('Template created');
            }
            setShowModal(false);
            setEditingTemplate(null);
            setForm({ name: '', content: '' });
            setSignatureData(null);
            loadTemplates();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (sigId) => {
        if (!window.confirm('Delete this signature template?')) return;
        try {
            await axios.delete(`${API}/admin/signatures/${sigId}`, { headers: getAuthHeader() });
            toast.success('Template deleted');
            loadTemplates();
        } catch { toast.error('Failed to delete template'); }
    };

    const openEdit = (template) => {
        setEditingTemplate(template);
        setForm({ name: template.name, content: template.content });
        setSignatureData(template.signature_data || null);
        setHtmlOnly(!madeInBuilder(template.signature_data));
        setEditorKey(k => k + 1);
        setShowModal(true);
    };

    const openCreate = () => {
        setEditingTemplate(null);
        setForm({ name: '', content: '' });
        setSignatureData(null);
        setHtmlOnly(false);
        setEditorKey(k => k + 1);
        setShowModal(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div data-testid="signature-templates-tab">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Email Signature Templates
                </h3>
                <Button onClick={openCreate} className="bg-stone-900 hover:bg-stone-800" data-testid="create-signature-template-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Create Template
                </Button>
            </div>
            <p className="text-sm text-stone-500 mb-6">
                Create signature templates that all users can access and customize for their emails.
            </p>

            {activeOverrideId && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-amber-600" />
                        <span className="text-sm text-amber-800">
                            <strong>Org Override Active</strong> — New emails start with this signature for everyone; people can still pick another or None
                        </span>
                    </div>
                    <button onClick={handleClearOverride} className="text-xs text-amber-700 hover:text-amber-900 underline">
                        Remove Override
                    </button>
                </div>
            )}

            {templates.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-200 rounded-xl">
                    <FileText className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                    <p className="text-stone-500 mb-3">No signature templates yet</p>
                    <Button variant="outline" onClick={openCreate}>
                        <Plus className="w-4 h-4 mr-1" /> Create First Template
                    </Button>
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                    {templates.map(template => (
                        <div
                            key={template.signature_id}
                            className="border border-stone-200 rounded-xl p-4 hover:border-stone-300 transition-colors"
                            data-testid={`signature-template-${template.signature_id}`}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h4 className="font-semibold text-stone-800">{template.name}</h4>
                                    <p className="text-xs text-stone-400 mt-0.5">
                                        Created by {template.created_by_name || 'Admin'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1">
                                    {activeOverrideId === template.signature_id ? (
                                        <>
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                                                <Shield className="w-3 h-3" /> Active Override
                                            </span>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-amber-700 hover:text-amber-900 hover:bg-amber-50"
                                                onClick={handleClearOverride}
                                            >
                                                <X className="w-3 h-3 mr-1" /> Remove
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                                            onClick={() => handleSetOverride(template.signature_id)}
                                        >
                                            <Shield className="w-3 h-3 mr-1" /> Set Override
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7"
                                        onClick={() => openEdit(template)}
                                    >
                                        <Edit3 className="w-3 h-3 mr-1" /> Edit
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                                        onClick={() => handleDelete(template.signature_id)}
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            </div>
                            <div
                                className="text-sm text-stone-600 prose prose-sm max-w-none line-clamp-3 border-t border-stone-100 pt-3"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.content) }}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit: the shared builder dialog (#970) */}
            <SignatureBuilderDialog
                open={showModal}
                onOpenChange={setShowModal}
                title={editingTemplate ? 'Edit signature template' : 'Create signature template'}
                description="Build it once. Each sender's details fill in when they send."
                isAdmin
                editorKey={editorKey}
                value={form.content}
                onChange={(html) => setForm(p => ({ ...p, content: html }))}
                signatureData={signatureData}
                onDataChange={setSignatureData}
                htmlOnly={htmlOnly}
                onHtmlChange={(html) => setForm(p => ({ ...p, content: html }))}
                name={form.name}
                onNameChange={(name) => setForm(p => ({ ...p, name }))}
                nameTestId="admin-signature-name-input"
                visibility={<VisibilityCards fixed="organization" idPrefix="admin-signature-visibility" />}
                onSave={handleSave}
                saving={saving}
                saveLabel={editingTemplate ? 'Update template' : 'Create template'}
                saveTestId="admin-save-signature-btn"
            />
        </div>
    );
}

export default function AdminSignaturesPage() {
    return (
        <AdminLayout title="Signatures" description="Create and manage email signature templates">
            <SignatureTemplatesTab />
        </AdminLayout>
    );
}
